import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp, type Step as StepId } from '../state/store';
import { Button } from './index';

export interface TourStep {
  /** Matches a data-tour="…" attribute on the page. Missing targets show the card in the middle. */
  target: string;
  title: string;
  body: string;
}

/** A few tips per screen, shown the first time that screen opens. */
export const TOURS: Partial<Record<StepId, TourStep[]>> = {
  start: [
    { target: 'video', title: 'Start with the full match', body: 'Pick or drop the whole match video, several GB is fine. It is read right here in your browser and never uploaded.' },
    { target: 'method', title: 'Choose how to find the highlights', body: 'Paste a timestamp list (a Veo list or your own times), or play the match and mark each highlight live.' },
    { target: 'privacy', title: 'Nothing leaves this device', body: 'Cutting and encoding run on your computer. Your project is saved in this browser, so you can close the tab and pick up later.' },
    { target: 'guide', title: 'Help is one click away', body: 'Open the guide for formats, shortcuts and tips. You can replay this tour from there too.' },
  ],
  timestamps: [
    { target: 'source', title: 'Say where the list comes from', body: 'Veo lists look like 02:5914s. Any other list works too: one time per line, with an optional length, range or label.' },
    { target: 'paste', title: 'Paste it as it is', body: 'Names, headings and empty lines are fine. Lines like “2nd period” switch the half.' },
    { target: 'found', title: 'Check what was read', body: 'Every line shows up here as you type. Skipped lines are marked in yellow with the reason, so nothing disappears silently.' },
  ],
  sync: [
    { target: 'player', title: 'Find each kickoff', body: 'Scrub to the moment the ball is first played. Space plays and pauses, the arrow keys step one second, Shift + arrow ten.' },
    { target: 'kickoffs', title: 'Set it at the playhead', body: 'Set the kickoff of each half. Then use “Check first clip” to see the first highlight of that half play.' },
    { target: 'clock', title: 'Clip early or late?', body: 'Veo’s 2nd-period clock doesn’t always restart at 45:00. Change the clock at kickoff until the first clip lines up.' },
  ],
  mark: [
    { target: 'markbtn', title: 'Press M to mark', body: 'Press once where a highlight starts and again where it ends. The clip goes straight into your reel.' },
    { target: 'speed', title: 'Go faster', body: 'Watch at 2× or 4× and slow down when something happens. Marks use the video time, so no kickoff sync is needed.' },
    { target: 'reel', title: 'Your reel so far', body: 'Click a clip to replay it or delete it. You can fine-tune every clip on the next step.' },
  ],
  clips: [
    { target: 'cliplist', title: 'Review every clip', body: 'Click to preview, untick to leave out, and nudge the start or end by a second. ↑ ↓ move between clips, P plays, X leaves out.' },
    { target: 'addclip', title: 'Add or trim at the playhead', body: 'Found a moment the list missed? Scrub to it and press A. Set start and Set end trim the selected clip to the playhead.' },
    { target: 'reelsum', title: 'Watch the total', body: 'The reel length updates as you go. Continue when it looks right.' },
  ],
  export: [
    { target: 'format', title: 'Pick a shape', body: 'Horizontal for YouTube and TV, vertical for Reels, TikTok and status, square for posts.' },
    { target: 'preview', title: 'Check the framing', body: 'For vertical and square, move the crop window or fit the whole picture with blurred bars.' },
    { target: 'exportbtn', title: 'Export', body: 'Chrome and Edge ask where to save and write the file straight to disk. Keep the tab open until it’s done.' },
  ],
};

const KEY = 'hl-tours-seen';

function seenSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}
function markSeen(id: string) {
  try {
    const s = seenSet();
    s.add(id);
    localStorage.setItem(KEY, JSON.stringify([...s]));
  } catch {
    /* storage blocked: the tour just shows again next time */
  }
}
export function resetTours() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_W = 340;
const PAD = 8;

/** Shows the current screen's tour the first time it opens, and again after "Replay the tour". */
export function TourHost() {
  const step = useApp((s) => s.step);
  const view = useApp((s) => s.view);
  const exporting = useApp((s) => s.exporting);
  const nonce = useApp((s) => s.tourNonce);
  const [active, setActive] = useState<{ id: StepId; i: number } | null>(null);
  const lastNonce = useRef(nonce);

  useEffect(() => {
    setActive(null);
    const forced = nonce !== lastNonce.current;
    lastNonce.current = nonce;
    if (view !== 'app' || exporting || !TOURS[step]) return;
    if (!forced && seenSet().has(step)) return;
    const t = window.setTimeout(() => setActive({ id: step, i: 0 }), forced ? 150 : 600);
    return () => window.clearTimeout(t);
  }, [step, view, exporting, nonce]);

  const close = useCallback(() => {
    if (active) markSeen(active.id);
    setActive(null);
  }, [active]);

  if (!active) return null;
  const steps = TOURS[active.id] ?? [];
  const s = steps[active.i];
  if (!s) return null;
  return (
    <TourCard
      key={`${active.id}-${active.i}`}
      step={s}
      index={active.i}
      count={steps.length}
      onBack={() => setActive({ ...active, i: active.i - 1 })}
      onNext={() => (active.i + 1 < steps.length ? setActive({ ...active, i: active.i + 1 }) : close())}
      onClose={close}
    />
  );
}

function TourCard({ step, index, count, onBack, onNext, onClose }: { step: TourStep; index: number; count: number; onBack: () => void; onNext: () => void; onClose: () => void }) {
  const [box, setBox] = useState<Box | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const [cardH, setCardH] = useState(180);

  useLayoutEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    let raf = 0;
    const loop = () => {
      const r = el?.getBoundingClientRect();
      setBox((prev) => {
        if (!r || r.width === 0) return null;
        const next = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
        return prev && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height ? prev : next;
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [step.target]);

  useLayoutEffect(() => {
    if (card.current) setCardH(card.current.offsetHeight);
    card.current?.focus();
  }, [step]);

  useEffect(() => {
    document.documentElement.dataset.tour = 'open';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') onNext();
      else if (e.key === 'ArrowLeft' && index > 0) onBack();
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      delete document.documentElement.dataset.tour;
      window.removeEventListener('keydown', onKey, true);
    };
  }, [index, onBack, onNext, onClose]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let pos: { top: number; left: number };
  if (!box) {
    pos = { top: vh / 2 - cardH / 2, left: vw / 2 - CARD_W / 2 };
  } else {
    const below = box.top + box.height + 12;
    const above = box.top - 12 - cardH;
    const top = below + cardH < vh - 16 ? below : above > 16 ? above : Math.max(16, Math.min(vh - cardH - 16, box.top + 16));
    const left = Math.max(16, Math.min(vw - CARD_W - 16, box.left + box.width / 2 - CARD_W / 2));
    pos = { top, left };
  }

  return createPortal(
    <div className="tour" role="presentation">
      <div className="tour-catch" onClick={onClose} />
      {box ? <div className="tour-spot" style={box} /> : <div className="tour-dim" />}
      <div ref={card} className="tour-card" role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-body" tabIndex={-1} style={{ top: pos.top, left: pos.left, width: CARD_W }}>
        <div className="app-row-between">
          <span className="app-label">
            Tip {index + 1} of {count}
          </span>
          <button type="button" className="tour-skip" onClick={onClose}>
            Skip tour
          </button>
        </div>
        <h2 id="tour-title" className="tour-title">
          {step.title}
        </h2>
        <p id="tour-body" className="tour-body">
          {step.body}
        </p>
        <div className="app-row-between">
          <span className="tour-dots" aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
              <span key={i} className={i === index ? 'is-on' : undefined} />
            ))}
          </span>
          <span className="app-row" style={{ gap: 8 }}>
            {index > 0 && (
              <Button size="sm" onClick={onBack}>
                Back
              </Button>
            )}
            <Button size="sm" variant="primary" onClick={onNext}>
              {index + 1 < count ? 'Next' : 'Got it'}
            </Button>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
